/**
 * Shared approval / rejection / deactivation logic for organisation accounts
 * (employer / sponsor / referrer).
 *
 * Background: the admin UI (PendingApprovalsManager) approves and rejects
 * organisations through a single unified endpoint
 * `POST /api/admin/accounts/:type/:id/status`, and reads history through
 * `GET /api/admin/accounts/:type/:id/audit-history`. The per-role endpoints
 * (`/api/admin/referrers/approve`, …) predate it. Everything here is pure and
 * unit-testable; the Firestore / email side effects live in server.ts, which
 * consumes these helpers so every role shares one code path.
 */
import { isValidStatusTransition } from './accountStatus';

export type OrgAccountType = 'employer' | 'sponsor' | 'referrer';

/** Status values as stored on the org documents. */
export type OrgStoredStatus = 'active' | 'rejected' | 'deactivated';

export const ORG_ACCOUNT_TYPES: readonly OrgAccountType[] = [
  'employer',
  'sponsor',
  'referrer',
];

/** Validate the `:type` path segment of the unified admin endpoint. */
export function normalizeOrgAccountType(type: unknown): OrgAccountType | null {
  const t = String(type ?? '')
    .trim()
    .toLowerCase();
  return (ORG_ACCOUNT_TYPES as readonly string[]).includes(t)
    ? (t as OrgAccountType)
    : null;
}

/**
 * Map the status vocabulary callers use to the stored vocabulary.
 * The admin UI sends `approved`; the documents store `active`.
 * Unknown values return null so callers answer 400 instead of silently
 * approving (the old per-role handlers mapped any unknown value to active).
 */
export function normalizeRequestedOrgStatus(
  status: unknown,
): OrgStoredStatus | null {
  const s = String(status ?? '')
    .trim()
    .toLowerCase();
  if (s === 'approved' || s === 'active') return 'active';
  if (s === 'rejected') return 'rejected';
  if (s === 'deactivated') return 'deactivated';
  return null;
}

/** Longest rejection reason surfaced to the rejected account owner. */
export const ORG_REJECTION_REASON_MAX_LENGTH = 1000;

/**
 * The rejection reason an org account's OWNER may see on their own
 * rejection notice (defect D-23).
 *
 * The admin reject action stores the reason on the org doc
 * (`rejectionReason`, written by applyOrgAccountStatusChange in
 * server.ts), but the owner-facing status endpoints never read it back,
 * so the notice always rendered its generic fallback text. This helper
 * is the single read rule those endpoints apply: it returns the trimmed
 * stored reason ONLY while the record is currently `rejected` — a stale
 * reason left on a since-approved record must never surface — capped at
 * ORG_REJECTION_REASON_MAX_LENGTH, and '' in every other case (callers
 * then omit the field and the notice keeps its generic text).
 *
 * Owner-only by construction: callers resolve the record from the
 * caller's own uid / org pointer before invoking this; it is never
 * applied to another user's record or to any list payload. The
 * deactivation reason is a separate field with its own surface (the
 * terminal-inactive screen) and is never returned here.
 */
export function orgRejectionReasonForRecord(
  record:
    | { status?: unknown; rejectionReason?: unknown; [key: string]: unknown }
    | null
    | undefined,
): string {
  if (!record) return '';
  const status = String(record.status ?? '')
    .trim()
    .toLowerCase();
  if (status !== 'rejected') return '';
  const reason =
    typeof record.rejectionReason === 'string'
      ? record.rejectionReason.trim()
      : '';
  return reason.slice(0, ORG_REJECTION_REASON_MAX_LENGTH);
}

/**
 * Validate a status change against the account-status state machine.
 * Missing/legacy statuses are treated as pending (approvable); identical
 * statuses are idempotent no-ops and always allowed.
 */
export function isValidOrgAccountStatusTransition(
  orgType: OrgAccountType,
  fromStatus: string | null | undefined,
  toStatus: OrgStoredStatus,
): boolean {
  if (!fromStatus || fromStatus === toStatus) return true;
  return isValidStatusTransition(orgType, fromStatus, toStatus);
}

export interface OrgStatusAuditEntryInput {
  orgType: OrgAccountType;
  orgId: string;
  orgName: string;
  fromStatus: string | null;
  toStatus: OrgStoredStatus;
  reason?: string | null;
  actorUid: string;
  actorEmail?: string | null;
  workspaceId?: string;
  now?: string;
}

export interface OrgStatusAuditEntry {
  action: string;
  targetId: string;
  orgType: OrgAccountType;
  orgName: string;
  fromStatus: string | null;
  toStatus: OrgStoredStatus;
  reason: string | null;
  details: string;
  timestamp: string;
  createdBy: string;
  actorEmail: string | null;
  workspaceId: string;
}

/**
 * Build the auditLogs payload for an org status change. Carries the
 * fromStatus/toStatus/reason/actorEmail fields the admin "Account Status
 * History" timeline renders (older entries only had a free-text action).
 */
export function buildOrgStatusAuditEntry(
  input: OrgStatusAuditEntryInput,
): OrgStatusAuditEntry {
  const now = input.now || new Date().toISOString();
  const reason = input.reason?.trim() ? input.reason.trim() : null;
  const verb =
    input.toStatus === 'active'
      ? 'approved'
      : input.toStatus === 'rejected'
        ? 'rejected'
        : 'deactivated';
  return {
    action: `${input.orgType}_${verb}`,
    targetId: input.orgId,
    orgType: input.orgType,
    orgName: input.orgName,
    fromStatus: input.fromStatus,
    toStatus: input.toStatus,
    reason,
    details:
      `${capitalize(input.orgType)} organization "${input.orgName}" ${verb} by ${input.actorUid}. ` +
      `Status: ${input.fromStatus || 'unknown'} → ${input.toStatus}.` +
      (reason ? ` Reason: ${reason}` : ''),
    timestamp: now,
    createdBy: input.actorUid,
    actorEmail: input.actorEmail || null,
    workspaceId: input.workspaceId || '',
  };
}

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function escapeHtml(s: string): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export interface OrgStatusEmailContent {
  subject: string;
  title: string;
  bodyHtml: string;
  ctaText: string;
  ctaUrl: string;
}

const TYPE_LABEL: Record<OrgAccountType, string> = {
  employer: 'Employer',
  sponsor: 'Sponsor',
  referrer: 'Referrer',
};

const APPROVAL_WELCOME_BODY: Record<OrgAccountType, string> = {
  employer:
    'You can now sign in to your dashboard to publish jobs, manage applications, and coordinate your hiring processes.',
  sponsor:
    'You can now sign in to your dashboard to manage exchange applicants, sponsors, and package processing.',
  referrer:
    'You can now sign in to your dashboard to submit referrals, track candidate progress, and communicate with partners.',
};

const APPROVAL_CONTACT_BODY: Record<OrgAccountType, string> = {
  employer:
    'Your team members can now access the recruiter dashboard to begin candidate recruitment.',
  sponsor:
    'Your team members can now access the coordinator dashboard to process applications.',
  referrer:
    'Your team members can now access the referrer dashboard to submit referrals.',
};

const APPROVAL_CONTACT_CTA: Record<OrgAccountType, string> = {
  employer: 'Access Recruiter Portal',
  sponsor: 'Access Sponsor Portal',
  referrer: 'Access Referrer Portal',
};

export interface OrgEmailOptions {
  /** Base login URL, e.g. `${FRONTEND_URL}/login`. */
  loginUrl: string;
  /** Rejection reason supplied by the admin (rejection email only). */
  reason?: string | null;
}

/**
 * Exact copy for the inactive-account banner shown to suspended users.
 * Rendered by the App.tsx deactivated-account gate; tested verbatim.
 */
export const INACTIVE_ACCOUNT_BANNER = {
  heading: 'Account Inactive',
  body: 'Your account is currently inactive. Please contact an administrator for further assistance.',
} as const;

/**
 * Build the users/{uid} update that suspends a linked team member when their
 * organisation is suspended. The App.tsx account gate keys off
 * users/{uid}.status === 'deactivated', so this is what actually removes
 * their access (no dashboard, no onboarding, no pending screen).
 * The previous status is preserved so unsuspend can restore it.
 */
export function buildUserSuspensionUpdate(
  prevStatus: string | null | undefined,
  orgId: string,
  now: string,
  orgType?: string,
): Record<string, string> {
  const update: Record<string, string> = {
    status: 'deactivated',
    statusBeforeOrgSuspension: prevStatus || 'active',
    suspendedOrgId: orgId,
    suspendedAt: now,
  };
  // PR-13b: record WHICH org type sourced the suspension so the auth
  // layer can classify it without a second lookup. Referrer-sourced
  // suspensions are capability restrictions, not account lockouts (see
  // classifyOrgSuspension). Legacy stamps lack this field; classifiers
  // fall back to resolving `suspendedOrgId` against the referrers
  // collection.
  if (orgType) update.suspendedOrgType = orgType;
  return update;
}

/**
 * PR-13b (product rule change, 2026-10-13): deactivating a REFERRER
 * restricts the referring capability only — it is NOT an account
 * lockout. The deactivated referrer still signs in, lands on their
 * dashboard read-only, can still act as / become an applicant, and has
 * referring actions refused server-side. Employer/sponsor deactivation
 * keeps the hard lockout (inactive screen + session kill). This exact
 * sentence is shown on the restricted dashboard banner, in the
 * re-apply refusal, and in referring-action refusal errors.
 */
export const REFERRER_RESTRICTED_NOTICE =
  "This account's referring has been restricted or archived — please contact admin for support.";

/**
 * Classify a user doc's org suspension.
 * - 'none': the user doc carries no deactivation stamp.
 * - 'hard': account-level lockout (employer/sponsor-sourced, or the
 *   sourcing org cannot be proven to be a referrer — fail closed).
 * - 'referrer-restricted': the suspension was sourced by a REFERRER org
 *   deactivation; the account stays usable and only referring is
 *   restricted.
 *
 * `suspendingOrgType` is the resolved type of the org named by
 * `suspendedOrgId` ('referrer' | 'employer' | 'sponsor' | null when the
 * org could not be resolved). The user doc's own `suspendedOrgType`
 * stamp (written by buildUserSuspensionUpdate since PR-13b) wins when
 * present; the resolved type covers legacy stamps.
 */
export function classifyOrgSuspension(
  userData: any,
  suspendingOrgType?: string | null,
): 'none' | 'hard' | 'referrer-restricted' {
  const status = userData?.status;
  if (status !== 'deactivated' && status !== 'disabled') return 'none';
  const stamped = typeof userData?.suspendedOrgType === 'string' ? userData.suspendedOrgType : '';
  const orgType = stamped || suspendingOrgType || '';
  return orgType === 'referrer' ? 'referrer-restricted' : 'hard';
}

/** True when this user doc was suspended by the given org and is still suspended. */
export function shouldRestoreUserOnUnsuspend(
  userData: any,
  orgId: string,
): boolean {
  return (
    !!userData &&
    userData.suspendedOrgId === orgId &&
    userData.status === 'deactivated'
  );
}

/** Values to restore on the user doc when their org is unsuspended. */
export function buildUserUnsuspendRestoreUpdate(
  userData: any,
  now: string,
): Record<string, string> {
  return {
    status:
      (userData && userData.statusBeforeOrgSuspension) || 'active',
    suspendedRestoredAt: now,
  };
}

/** Welcome email sent to each linked team member when their org is approved. */
export function buildOrgApprovalWelcomeEmail(
  orgType: OrgAccountType,
  orgName: string,
  opts: OrgEmailOptions,
): OrgStatusEmailContent {
  const label = TYPE_LABEL[orgType];
  const safeName = escapeHtml(orgName);
  return {
    subject: `Your ${label} Account has been Approved!`,
    title: 'Workspace Approved',
    bodyHtml:
      `<p>Congratulations! Your ${orgType} account for <strong>${safeName}</strong> ` +
      `has been reviewed and successfully approved by our administrative team.</p>` +
      `<p>${APPROVAL_WELCOME_BODY[orgType]}</p>`,
    ctaText: 'Access Portal',
    ctaUrl: opts.loginUrl,
  };
}

/** Approval email sent to the primary contact when it is not a linked user. */
export function buildOrgApprovalContactEmail(
  orgType: OrgAccountType,
  orgName: string,
  opts: OrgEmailOptions,
): OrgStatusEmailContent {
  const label = TYPE_LABEL[orgType];
  const safeName = escapeHtml(orgName);
  return {
    subject: `Your ${label} Organization has been Approved!`,
    title: 'Organization Approved',
    bodyHtml:
      `<p>Congratulations! Your ${orgType} organization <strong>${safeName}</strong> ` +
      `has been approved for access to our workspace.</p>` +
      `<p>${APPROVAL_CONTACT_BODY[orgType]}</p>`,
    ctaText: APPROVAL_CONTACT_CTA[orgType],
    ctaUrl: opts.loginUrl,
  };
}

/** Rejection email sent to the org's contact email. Includes the admin's reason. */
export function buildOrgRejectionEmail(
  orgType: OrgAccountType,
  orgName: string,
  opts: OrgEmailOptions,
): OrgStatusEmailContent {
  const label = TYPE_LABEL[orgType];
  const safeName = escapeHtml(orgName);
  const reason = opts.reason?.trim() ? opts.reason.trim() : '';
  return {
    subject: `${label} Organization Application Status Update`,
    title: 'Application Update',
    bodyHtml:
      `<p>Thank you for submitting your ${orgType} organization details for <strong>${safeName}</strong>.</p>` +
      `<p>Unfortunately, your application could not be approved at this time.` +
      (reason
        ? ` Our team left the following note: <em>"${escapeHtml(reason)}"</em></p>`
        : `</p>`) +
      `<p>Please contact our support team if you believe this is in error or need additional details.</p>`,
    ctaText: '',
    ctaUrl: '',
  };
}
